import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { unlockScanSound } from "./scanSound";

const isEditable = (el) =>
  !!el &&
  (el.matches?.("input, textarea, select, [contenteditable='true']") ||
    !!el.closest?.(".modal, .swal2-container, [role='dialog'], [role='listbox']"));

/**
 * Ô nhận mã từ máy quét mã vạch (máy quét gõ chuỗi + Enter/Tab) hoặc paste.
 * Paste: kiểm tra ngay, không cần Enter; paste nhiều mã (mỗi dòng một mã) cũng được.
 * Luôn giữ focus (BRD §3): tự lấy lại focus khi click ra vùng trống, khi quay lại tab,
 * và khi người dùng gõ phím lúc focus đang ở body. Không giành focus khỏi ô nhập khác / hộp thoại.
 */
const ScannerInput = forwardRef(function ScannerInput(
  { enabled = true, onScan, placeholder = "Quét hoặc paste mã vận đơn...", disabledPlaceholder },
  ref
) {
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));

  useEffect(() => {
    if (enabled) inputRef.current?.focus();
  }, [enabled]);

  useEffect(() => {
    const reclaim = () => {
      if (!enabledRef.current) return;
      const active = document.activeElement;
      if (active === inputRef.current) return;
      if (active && active !== document.body && isEditable(active)) return;
      inputRef.current?.focus();
    };
    const onKeyDown = (event) => {
      unlockScanSound();
      if (!enabledRef.current) return;
      const active = document.activeElement;
      if (active === inputRef.current) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Ô chỉ đọc (vd danh sách mã để copy) vẫn chuyển phím quét về ô nhận mã.
      if (isEditable(active) && !active.readOnly) return;
      // Ký tự đầu tiên của máy quét khi focus đang lạc: chuyển focus về ô quét để nhận tiếp.
      inputRef.current?.focus();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") setTimeout(reclaim, 50);
    };
    const onFocusOut = () => setTimeout(reclaim, 150);
    const onPointerDown = () => unlockScanSound();

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  const submit = () => {
    const code = value.trim();
    setValue("");
    if (code && enabledRef.current) onScan(code);
  };

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData("text") || "";
    event.preventDefault();
    const combined = `${value} ${text}`.trim();
    setValue("");
    if (combined && enabledRef.current) onScan(combined);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || (event.key === "Tab" && value.trim())) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className="form-control warehouse-scan-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      disabled={!enabled}
      placeholder={enabled ? placeholder : disabledPlaceholder || placeholder}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      inputMode="none"
      aria-label="Ô nhận mã quét"
    />
  );
});

export default ScannerInput;
